import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anonymous

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    localStorage.removeItem("ll_token"); // clear any legacy token storage
    loadMe(); // session lives in the httpOnly cookie
  }, [loadMe]);

  // WS auth rejection (expired cookie) -> drop the session in the UI
  useEffect(() => {
    const onExpired = () => setUser(false);
    window.addEventListener("ll:auth-expired", onExpired);
    return () => window.removeEventListener("ll:auth-expired", onExpired);
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.debug("[auth] logout request failed", e); }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, loadMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
